<template>
  <q-card flat bordered>
    <q-card-section>
      <div class="row justify-between">
        <div>
          <div class="text-h6">Metadata</div>
          <div>
            Add custom metadata to your report. This won't be displayed in the report, but can be
            used to store additional information. Reports retrieved from the API will include this
            metadata.
          </div>
        </div>
      </div>
    </q-card-section>
    <q-separator />
    <q-card-section>
      <div v-if="newValues.length === 0">
        <q-banner
          rounded
          :class="$q.dark.isActive ? 'bg-grey-9 text-grey-1' : 'bg-grey-3 text-grey-8'"
        >
          <q-icon name="info" />
          No metadata added yet.
        </q-banner>
      </div>
      <div v-else>
        <div v-for="(value, index) in newValues" :key="index" class="row items-center q-mb-sm">
          <q-select
            :model-value="value.key"
            label="Key"
            outlined
            dense
            class="col q-mr-sm"
            use-input
            fill-input
            hide-selected
            input-debounce="0"
            hide-dropdown-icon
            :options="filteredMetadataKeys"
            new-value-mode="add-unique"
            @input-value="(val) => (value.key = val)"
            @blur="emitNewValue"
            @filter="filterMetadataKeys"
          />
          <q-input
            v-model="value.value"
            label="Value"
            outlined
            dense
            @blur="emitNewValue"
            class="col q-mr-sm"
          />
          <q-btn icon="remove" flat round dense @click="removeMetadata(index)" class="col-auto" />
        </div>
      </div>
      <q-banner
        rounded
        dense
        v-if="error"
        :class="$q.dark.isActive ? 'bg-red-10 text-red-1' : 'bg-red-1 text-red-9'"
      >
        {{ error }}
      </q-banner>
    </q-card-section>
    <q-separator />
    <q-card-actions align="right">
      <q-btn no-caps outline @click="addMetadata">Add Metadata</q-btn>
    </q-card-actions>
  </q-card>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { usePropertyReportStore } from '@/stores/propertyreport'
export default {
  name: 'EditMetadata',
  components: {},
  props: {
    metadata: {
      type: Object as PropType<Record<string, string | null>>,
      default: () => ({}),
      description: 'Metadata to edit'
    }
  },
  emits: ['input'],
  data() {
    return {
      newValues: [] as { key: string | null; value: string | null }[],
      filteredMetadataKeys: [] as string[]
    }
  },
  mounted() {
    this.filteredMetadataKeys = this.metadataKeys
  },
  computed: {
    metadataKeys(): string[] {
      return usePropertyReportStore().activeSubscriptions.reduce((acc: string[], curr) => {
        if (curr.metadata) {
          const keys = Object.keys(curr.metadata)
          keys.forEach((key) => {
            if (!acc.includes(key) && !this.metadata.hasOwnProperty(key)) {
              acc.push(key)
            }
          })
        }
        return acc
      }, [] as string[])
    },
    error(): string | null {
      // if any of the new values are empty, return true
      if (this.newValues.some((value) => value.key === null || value.value === null)) {
        return 'Please fill in all fields'
      }

      // if there are duplicate keys, return true
      if (
        this.newValues.some((value, index) =>
          this.newValues.slice(0, index).some((v) => v.key === value.key)
        )
      ) {
        return 'Duplicate keys are not allowed'
      }

      return null
    }
  },
  created() {
    this.newValues = Object.entries(this.metadata).map(([key, value]) => ({ key, value })) as {
      key: string | null
      value: string | null
    }[]
  },
  methods: {
    addMetadata() {
      this.newValues.push({ key: null, value: null })
    },
    removeMetadata(index: number) {
      this.newValues.splice(index, 1)
      this.emitNewValue()
    },
    emitNewValue() {
      // Filter out entries with null keys or values before emitting
      const validEntries = this.newValues.filter(
        (entry) => entry.key !== null && entry.key !== '' && entry.value !== null
      )
      const cleanedMetadata = Object.fromEntries(
        validEntries.map((entry) => [entry.key?.trim() as string, entry.value?.trim()])
      )
      this.$emit('input', cleanedMetadata)
    },
    filterMetadataKeys(val: string, update: (callbackFn: () => void) => void) {
      if (val === '') {
        update(() => {
          this.filteredMetadataKeys = this.metadataKeys
        })
        return
      }
      update(() => {
        const query = val.toLowerCase().split(' ').join('')
        this.filteredMetadataKeys = this.metadataKeys.filter((key) =>
          key.toLowerCase().includes(query)
        )
      })
    }
  }
}
</script>

<style scoped lang="scss"></style>
